import type { HeatmapInstrument as HeatmapInstrumentT } from 'lib-analytics-types';
import isEqual from 'lodash/isEqual';
import isNumber from 'lodash/isNumber';
import { useCallback, useLayoutEffect, useMemo, useRef } from 'react';
import { useMap } from 'react-use';

import { getSeriesValue } from '../chartInstrument/utils/dataUtils';

import {
  HeatmapInstrumentCell,
  HeatmapInstrumentCellId,
  HeatmapInstrumentCell as HeatmapInstrumentCellT,
} from './components/cell/heatmapInstrumentCell';
import styles from './heatmapInstrument.module.scss';
import {
  calculatePercentagePoint,
  extractMinMax,
  getHeatmapCellStyles,
} from './utils';

export type HeatmapInstrumentProps = {
  /** The heatmap instrument. */
  instrument: HeatmapInstrumentT;
};

export const HeatmapInstrument = ({
  instrument,
}: HeatmapInstrumentProps): JSX.Element | null => {
  const {
    data: { categories: rows, series: columns },
  } = instrument;

  const rootRef = useRef<HTMLDivElement | null>(null);

  const [highlights, highlightSetters] = useMap<{
    // The highlighted cell.
    cell: HeatmapInstrumentCellT | null;
    // The index of the highlighted column.
    column: number | null;
    // The index of the highlighted row.
    row: number | null;
  }>({
    cell: null,
    column: null,
    row: null,
  });

  /** Whether anything (cell, column or row) is currently highlighted. */
  const hasHighlights = useMemo(
    () => Object.values(highlights).some((v) => v !== null),
    [highlights],
  );

  /** The minimum and maximum value of the heatmap dataset. */
  const minMax = useMemo(() => extractMinMax(columns), [columns]);

  // Adds the event listener that checks whether the mouse is outside of the root element and resets
  // the highlights if there are any.
  useLayoutEffect(() => {
    const handleMouseMove = (e) => {
      if (rootRef.current) {
        const isOutsideRoot = !rootRef.current.contains(e.target);

        if (isOutsideRoot && hasHighlights) {
          highlightSetters.reset();
        }
      }
    };

    window.addEventListener('mousemove', handleMouseMove, true);

    return () => {
      window.removeEventListener('mousemove', handleMouseMove, true);
    };
  }, [hasHighlights, highlightSetters]);

  /** Returns whether a cell is dimmed (not highlighted). */
  const isCellDimmed = useCallback(
    (cellId: HeatmapInstrumentCellId) => {
      if (!hasHighlights) {
        return false;
      }

      const [rowIndex, columnIndex] = cellId;

      if (highlights.cell) {
        return !isEqual(highlights.cell.id, cellId);
      }
      if (highlights.column !== null) {
        return highlights.column !== columnIndex;
      }
      if (highlights.row !== null) {
        return highlights.row !== rowIndex;
      }
      return false;
    },
    [hasHighlights, highlights.cell, highlights.column, highlights.row],
  );

  /** Handles highlighting an entire column when hovering it. */
  const handleHoverColumn = useCallback(
    (column: number) =>
      highlightSetters.setAll({
        cell: null,
        column,
        row: null,
      }),
    [highlightSetters],
  );

  /** Handles highlighting an entire row when hovering it. */
  const handleHoverRow = useCallback(
    (row: number) =>
      highlightSetters.setAll({
        cell: null,
        column: null,
        row,
      }),
    [highlightSetters],
  );

  /** Handles highlighting a cell when hovering it. */
  const handleHoverCell = useCallback(
    (cell: HeatmapInstrumentCell | null) => {
      if (hasHighlights || cell !== null) {
        highlightSetters.setAll({
          cell,
          column: null,
          row: null,
        });
      }
    },
    [hasHighlights, highlightSetters],
  );

  /** Handles resetting any highlights when hovering the dummy cell. */
  const handleHoverDummyCell = useCallback(() => {
    if (hasHighlights) {
      highlightSetters.reset();
    }
  }, [hasHighlights, highlightSetters]);

  return (
    <div
      ref={rootRef}
      className={styles.grid}
      onMouseLeave={() => highlightSetters.reset()}
      data-testid="heatmap-instrument"
    >
      <div className={styles.row}>
        <HeatmapInstrumentCell onMouseEnter={handleHoverDummyCell} />

        {columns.map((column, columnIndex) => {
          const columnMetrics = columns[columnIndex].metrics ?? [];

          return (
            <HeatmapInstrumentCell
              key={column.label}
              cell={{ id: [0, columnIndex], value: column.label }}
              tooltipProps={{
                enabled: columnMetrics.length > 0,
                title: column.label,
                metrics: columnMetrics,
              }}
              onMouseEnter={() => handleHoverColumn(columnIndex)}
            />
          );
        })}
      </div>

      {rows.map((row, rowIndex) => {
        const rowMetrics = rows[rowIndex].metrics ?? [];

        return (
          <div key={row.label} className={styles.row}>
            <HeatmapInstrumentCell
              cell={{ id: [rowIndex, 0], value: row.label }}
              tooltipProps={{
                enabled: rowMetrics.length > 0,
                title: row.label,
                metrics: rowMetrics,
              }}
              onMouseEnter={() => handleHoverRow(rowIndex)}
            />

            {columns.map((column, columnIndex) => {
              const id: HeatmapInstrumentCellId = [rowIndex, columnIndex];
              const { value, metrics } = getSeriesValue(column.data[rowIndex]);
              const format = column.options?.format ?? 'number';
              const hasValue = isNumber(value);

              const { backgroundColor, color } = getHeatmapCellStyles(
                hasValue ? calculatePercentagePoint(value, minMax) : null,
              );

              return (
                <HeatmapInstrumentCell
                  key={id.join(',')}
                  cell={{ id, value, format }}
                  dimmed={isCellDimmed(id)}
                  background={backgroundColor}
                  tooltipProps={{
                    enabled: hasValue,
                    title: `${row.label}, ${column.label}`,
                    metrics: metrics?.length
                      ? metrics
                      : [
                          {
                            type: 'metric',
                            title: 'Total',
                            data: value,
                            format: format === 'count' ? 'number' : format,
                          },
                        ],
                  }}
                  textProps={{ color }}
                  onMouseEnter={() =>
                    handleHoverCell(hasValue ? { id, value } : null)
                  }
                />
              );
            })}
          </div>
        );
      })}
    </div>
  );
};
